'use client'

import { reportHandledException } from '@/lib/error-reporting'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Mic, Square } from 'lucide-react'
import { Button, cn } from '@kiao-voice/ui-kit'
import {
  PAIR_FIELD_CLASS,
  PAIR_FRAME_CLASS,
  PAIR_GRID_CLASS,
  PAIR_SPEECH_CLASS,
  PairArrow,
  PairLabel,
} from '../site/pair-frame'
import { REVEAL_CLASS } from '../site/reveal'
import { LandingDemoApiError, submitAnonymousDemo } from '../../features/landing-demo/api-client'
import {
  createRealtimeDemoClient,
  RealtimeDemoRateLimitedError,
  type RealtimeDemoClient,
} from '../../features/landing-demo/realtime-demo-client'
import { ANALYTICS_EVENTS, trackEvent } from '../../lib/analytics'
import { DEMO_MIC_CONSTRAINTS } from '../../lib/mic-capture'
import { raceWithTimeout } from '../../lib/race-with-timeout'
import { useMicRecorder } from '../../lib/use-mic-recorder'
import { usePcmStream, type PcmStartResult } from '../../lib/use-pcm-stream'
import { revealDelay, useReveal } from '../../lib/use-reveal'
import { createTimerPool, typeText, type TimerPool } from './demo-typing'

/* 波形長條 — prototype 的決定式「隨機」公式（28 條） */
const WAVE_BARS = Array.from({ length: 28 }, (_, index) => ({
  delay: `${(index * 47) % 700}ms`,
  height: (0.3 + ((index * 7) % 10) / 14).toFixed(2),
}))

/** 批次潤飾結果逐字打出的節奏（ms / 字），對齊舊腳本示範。 */
const TYPE_SPEED_MS = 26

/**
 * take 結束後保留 warm 麥克風的閒置時間。跨 take(錄音 → 整理 → 打字 → done → 再試一次)
 * 重用同一個 getUserMedia stream,省掉每次重開的 ~100–400ms(那段時間沒有任何音訊,
 * 語句開頭會被吃掉)。超過這段時間沒有新 take 才真正釋放(停 tracks)。
 */
const MIC_IDLE_RELEASE_MS = 30_000

/**
 * pcmStream.start 的內建逾時(傳給 hook 的 { timeoutMs })。warmup 後正常啟動 ~10–30ms,
 * 3s 已是異常(addModule 網路卡住 / resume 懸置);逾時由 hook 內部關 context、回 {ok:false},
 * 讓本 take 走 recorder-only(仍可錄音/停止/走批次),絕不讓 Promise.all 永不 settle。
 */
const PCM_START_TIMEOUT_MS = 3_000

/** stopRecording 等 begin 決定的上限:念一句通常已完成;token fetch 卡住 10s 後當 batch。 */
const BEGIN_DECISION_TIMEOUT_MS = 10_000

/** batch 路徑等 recorder onStop 產出 WAV 的上限;逾時當作沒有 blob。 */
const BLOB_WAIT_TIMEOUT_MS = 3_000

/**
 * 單一 take 的最長時長(硬上限)。過去寄生在 recorder 的自動停;但 recorder 啟動失敗
 * 而 PCM 正常時 realtime 會無限串流。改由 take 自持一顆計時器,到期就結束整個 take。
 *
 * 85s 而非伺服器的 90s 上限:示範句是一段有反悔、有插話的口語 brain dump,
 * 15s 根本念不完。留 5s 餘裕給停止 → 上傳的往返,讓 client 一定先於 server 收手,
 * 使用者看到的是「示範結束」而不是後端丟回來的長度錯誤。
 */
const TAKE_MAX_DURATION_MS = 85_000

/** mic acquire(getUserMedia,含權限彈窗)的上限;懸置逾時就當作失敗,不讓 'starting' 永久卡住。 */
const MIC_ACQUIRE_TIMEOUT_MS = 30_000

/**
 * 波形視覺增益。analyser 的頻譜平均(0..1)對一般人聲偏低(~0.1–0.3),bar 會很矮、
 * 看起來沒動感。放大這個顯示值讓錄音更有生氣。**純視覺** — 波形讀的是 analyser,
 * 辨識送的是 worklet 的 PCM,兩條完全分開,所以這不影響辨識,只影響好不好看。
 */
const WAVE_VISUAL_GAIN = 2.6

// 'starting' = 已按下、擷取正在啟動(getUserMedia + worklet)但尚未真的在收音 —— 消除
// 「按了卻沒任何回饋」的死區;兩路擷取 settle 後才轉 'recording'。
type DemoPhase = 'idle' | 'starting' | 'recording' | 'processing' | 'typing' | 'done'

const MIC_LABEL_KEY: Record<DemoPhase, string> = {
  idle: 'micIdle',
  starting: 'micStarting',
  recording: 'micRecording',
  processing: 'micProcessing',
  typing: 'micProcessing',
  done: 'micRetry',
}

const STATUS_KEY_BY_PHASE: Record<DemoPhase, string> = {
  idle: 'statusIdle',
  starting: 'statusStarting',
  recording: 'statusRecording',
  processing: 'statusProcessing',
  typing: 'statusProcessing',
  done: 'statusDone',
}

/** 卡片下方的可見錯誤通知（.alert）。 */
type DemoNotice = 'rate-limited' | 'no-speech' | 'error' | null

/** 一個外部可 resolve 的 promise(每個 take 一份;recorder onStop 於此 resolve 出 WAV)。 */
interface Deferred<T> {
  promise: Promise<T>
  resolve: (value: T) => void
}

function createDeferred<T>(): Deferred<T> {
  let resolve!: (value: T) => void
  const promise = new Promise<T>((r) => {
    resolve = r
  })
  return { promise, resolve }
}

/**
 * Hero 的互動語音輸入示範卡。**這張卡從不自己演**:輸出槽常駐可見但留空,
 * 只有一支游標在等,旁邊一個指示標指著按鈕 —— 全卡唯一會動的就是那 2px 游標。
 * (round-1 A/B 的結論:會自我循環播放的卡片被讀成動畫而不是控制項 —— 522 個看到
 * 的人只有 20 個按過。靜態邀請版勝出並在此永久化,實驗與 autoplay 已整個移除。)
 *
 * 每次點擊都會做「真擷取」,採 tap-to-start:
 * 按下即進入錄音(不等 relay 連線),relay 連線在背景進行;連上前的 PCM frame
 * 先 buffer、連上後補送,所以連線期間講的話不會遺失。**錄音中不顯示任何文字**
 * (partials 是 enhancement 前的原始 STT 毛胚,刻意不渲染);再按一次停止 →
 * 整理中 → 把**最終潤飾結果**逐字打進單一 `.demo-out`。realtime 不可用時退回
 * 批次:停止後上傳 `submitAnonymousDemo`,同樣只打出潤飾結果。兩條路徑對使用者
 * 一致:錄音 → 整理 → 打出潤飾結果,看不到中間毛胚字。
 *
 * **單一麥克風擷取**:blob recorder 與 PCM worklet 共用同一個 `getUserMedia`
 * stream(關閉 echoCancellation/noiseSuppression/autoGainControl,對齊 desktop
 * 原始擷取)。對同一支實體麥克風開兩路擷取會在真實硬體上互搶、其中一路收到損毀
 * 音訊 → STT 亂碼(曾在 prod 產生「777777」)。realtime 是否上線由 relay token
 * 端點回傳的 relayUrl 決定(prod 已配置);每條失敗路徑都有可見的通知或 fallback,
 * 沒有靜默吞錯。
 */
export function DictationDemoCard() {
  const { t, i18n } = useTranslation(['web'])
  const reveal = useReveal()

  const [phase, setPhase] = useState<DemoPhase>('idle')
  const [output, setOutput] = useState('')
  const [notice, setNotice] = useState<DemoNotice>(null)
  const [liveWave, setLiveWave] = useState(false)
  // 使用者是否已點擊接管 → 從靜態邀請切換成真實收音的狀態顯示。
  const [takenOver, setTakenOver] = useState(false)

  // hero_demo_result_shown — the visitor's own dictation produced a transcript
  // (the demo "aha"). `phase` only advances in real (taken-over) mode, so this
  // is a genuine result; `notice === null` excludes the error / no-speech
  // dead-ends. Re-armed on the next recording so "try again" counts again.
  const demoResultTrackedRef = useRef(false)
  useEffect(() => {
    if (phase === 'recording') demoResultTrackedRef.current = false
    if (phase === 'done' && notice === null && !demoResultTrackedRef.current) {
      demoResultTrackedRef.current = true
      trackEvent(ANALYTICS_EVENTS.heroDemoResultShown)
    }
  }, [phase, notice])

  const pcmStream = usePcmStream()
  const realtimeRef = useRef<RealtimeDemoClient | null>(null)
  const realtimeActiveRef = useRef(false)
  const capturedBlobRef = useRef<Blob | null>(null)
  // 每個 take 一份的「WAV 就緒」promise:recorder onStop 於此 resolve 出 blob(或 null)。
  // stopRecording 走批次時 await 它(bounded),取代舊的「onStop 自行上傳」— 上傳決策集中
  // 在 stopRecording 一處,避免 onStop 與 begin('realtime') 雙所有權互搶(Codex#3)。
  const blobReadyRef = useRef<Deferred<Blob | null> | null>(null)
  const typePoolRef = useRef<TimerPool | null>(null)
  const waveRef = useRef<HTMLDivElement | null>(null)
  const rafRef = useRef<number | null>(null)
  const unsubscribeFrameRef = useRef<(() => void) | null>(null)
  // 每次錄音的世代序號:realtime 回呼會記下啟動當下的世代,
  // 晚到(來自已 dispose / 前一個 session)的 onFinal/onError 一律忽略(H2)。
  const sessionGenRef = useRef(0)
  // 單一所有權守衛:每個世代只允許一次 submitAnonymousDemo,避免
  // recorder.onStop 與 realtime onError('unavailable') 同時上傳(C2)。
  const batchSubmittedRef = useRef(false)
  // 共享單一麥克風擷取:blob recorder 與 PCM worklet 共用同一 MediaStream。對同一支
  // 實體麥克風開兩路 getUserMedia 會在真實硬體互搶,其中一路收到損毀/靜音音訊 →
  // STT 亂碼(曾在首頁 demo 產生「777777」)。由本元件擁有並在錄音結束時釋放。
  const sharedStreamRef = useRef<MediaStream | null>(null)
  // tap-to-start:relay 連線在背景進行。連上(ready)前把 worklet 產生的 frame 先在
  // 這裡累積,session 建立後一次補送(session 自身再 buffer 到 ws ready)。確保按下
  // 當下起的每個 frame 都不丟,也不必等連線才開始錄。
  const pendingFramesRef = useRef<string[]>([])
  // 背景 realtime 連線決定的 promise('realtime' | 'batch' | 'rate-limited');
  // stopRecording 會 await 它(念一句通常已完成)以決定收尾方式。
  const beginPromiseRef = useRef<Promise<'realtime' | 'batch' | 'rate-limited'> | null>(null)
  // blob recorder 是否真的啟動:決定停止時能否走批次(需要 recorder.onStop 的 blob),
  // 也決定共享麥克風要不要靠 onStop 釋放(recorder 沒啟動時 onStop 不會 fire)。
  const recorderStartedRef = useRef(false)
  // recorder 啟動成功時戳上的世代:onStop 用它擋掉「上一 take 超遲 onstop 對上本 take gen」
  // 的殘餘路徑(RC1b)—— 遲到 blob 不 resolve blobReady、不觸發 auto-stop、不進 S12 安全網。
  const recorderGenRef = useRef(-1)
  // 「本 take 已發起 stopRecording」(手動點停止 + recorder 自動停共用)。onStop 用它判斷該不該
  // 把 recorder 自動停升級成「結束整個 take」(避免對已在停止流程中的 take 重入)。
  const stopRequestedRef = useRef(false)
  // 進行中的 getUserMedia(pointerdown 預熱或 startRecording 開的)。單一擷取不變式:
  // 有 live warm stream 或此 promise 進行中時,絕不再開第二個 getUserMedia。
  const micAcquirePromiseRef = useRef<Promise<MediaStream | null> | null>(null)
  // warm 麥克風的閒置釋放計時器(30s);新 take / pointerdown 預熱會清掉並重置。
  const idleReleaseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // 單一 take 的硬上限計時器(TAKE_MAX_DURATION_MS):進 'recording' 時 arm,到期結束整個 take;
  // stopRecording entry / abortRecording / unmount 清除(見 TAKE_MAX_DURATION_MS)。
  const takeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // 永遠指向最新的 armIdleReleaseTimer,讓計時器回呼「錄音中→重新 arm」不自我依賴(免 deps 循環)。
  const armIdleReleaseTimerRef = useRef<() => void>(() => {})
  // 解除 warm stream 的 'ended' 監聽(權限被撤/裝置被拔)— 釋放時呼叫並清為 null。
  const detachEndedRef = useRef<(() => void) | null>(null)
  // double-start 守衛:startRecording 於 entry → phase flip 之間上鎖,此窗內的點擊一律
  // 被 handleMicClick 忽略(否則會重入 startRecording、以世代跳號丟掉第一段錄音)。
  const startInFlightRef = useRef(false)
  // 元件是否已卸載:in-flight 的 getUserMedia 於 unmount 後才 resolve 時,不可把 stream
  // 收進已卸載的 ref(否則麥克風永久洩漏)— commit 前檢查,已卸載就停掉那條 stream。
  const unmountedRef = useRef(false)
  // phase 的即時鏡像(供在 effect / 非同步回呼裡讀最新 phase,不吃 render 閉包的舊值)。
  const phaseRef = useRef<DemoPhase>('idle')
  // RC2:同步鏡像 —— phaseRef 立刻更新(不等 passive effect flush),消滅整類「非 React 回呼
  // 讀 stale phase」競態(如 acquire 逾時後遲到 commit 讀到 stale 'starting' 而跳過 park)。
  // 所有 setPhase 呼叫點一律改走 setPhaseSync。
  const setPhaseSync = useCallback((next: DemoPhase) => {
    phaseRef.current = next
    setPhase(next)
  }, [])

  const recorder = useMicRecorder({
    maxDurationMs: TAKE_MAX_DURATION_MS, // RC4:take cap 單一事實來源(取代 hook DEFAULT,免漂移)
    onStop: (blob) => {
      // RC1b:上一 take 的超遲 onstop 對上本 take 的 gen → 丟棄(不 resolve blobReady、不觸發
      // auto-stop、不進 S12 安全網),否則會把 N 的音檔/逐字稿污染進 N+1。
      if (recorderGenRef.current !== sessionGenRef.current) {
        console.warn('[landing-demo] late recorder blob from a previous take — dropped')
        return
      }
      // onStop 不再自行走一般上傳路徑:存 blob、resolve 本 take 的 blobReady(供 stopRecording
      // 的批次分支消費)、park、偵測 recorder 自動停 = 結束整個 take(恢復硬上限)。
      capturedBlobRef.current = blob
      blobReadyRef.current?.resolve(blob)
      // S12 安全網:blob 晚於 3s awaitBlob 才到(大 WAV / 慢裝置)→ take 已進 'done'+error,好錄音
      // 會永不上傳。受控補救:停止已發起、無其他終局所有權、且已進 done 時補上傳一次
      // (batchSubmittedRef 單一所有權保證不與 no_speech / rate-limited / realtime final 衝突)。
      if (
        stopRequestedRef.current &&
        !batchSubmittedRef.current &&
        !realtimeActiveRef.current &&
        phaseRef.current === 'done'
      ) {
        void runBatchSubmit(blob)
      }
      parkSharedStream()
      // MediaRecorder 到 maxDurationMs 自動停:若仍在錄音且尚未發起停止(手動點停止會先設 stopRequestedRef)
      // → 由此結束整個 take,realtime 不再無限續流(與 take 自持計時器 S2 互為 belt)。
      if (phaseRef.current === 'recording' && !stopRequestedRef.current) void stopRecording()
    },
  })

  const stopWaveLoop = useCallback(() => {
    setLiveWave(false)
    if (rafRef.current !== null && typeof window !== 'undefined') {
      window.cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
  }, [])

  const clearTypePool = useCallback(() => {
    typePoolRef.current?.clear()
    typePoolRef.current = null
  }, [])

  /** 擷取是否進行中(啟動中 / 錄音中 / startRecording 尚未翻 phase)。三處共用的守衛:
   *  idle-timer 重 arm、visibilitychange 釋放、pointerdown 預熱 arm。 */
  const isCaptureBusy = useCallback(
    () =>
      phaseRef.current === 'recording' ||
      phaseRef.current === 'starting' ||
      startInFlightRef.current,
    []
  )

  /** 清掉 warm 麥克風的閒置釋放計時器。冪等。 */
  const clearIdleReleaseTimer = useCallback(() => {
    if (idleReleaseTimerRef.current !== null) {
      clearTimeout(idleReleaseTimerRef.current)
      idleReleaseTimerRef.current = null
    }
  }, [])

  /** 清掉 take 硬上限計時器。冪等。 */
  const clearTakeTimer = useCallback(() => {
    if (takeTimerRef.current !== null) {
      clearTimeout(takeTimerRef.current)
      takeTimerRef.current = null
    }
  }, [])

  /**
   * 立即釋放共享麥克風:清閒置計時器、解 'ended' 監聽、停 tracks、清 ref,並 stop PCM
   * (冪等:無 session 時只 discard warm cache)—— 讓 pointerdown 預熱建的 warm AudioContext
   * 也隨麥克風釋放一起回收,不會一直掛在頁面上。用於 unmount / rate-limited abort /
   * 閒置逾時 / 切走分頁 / track 'ended'。
   */
  const releaseSharedStream = useCallback(() => {
    clearIdleReleaseTimer()
    detachEndedRef.current?.()
    detachEndedRef.current = null
    // 一併拆掉 realtime 的 frame 橋接:麥克風已沒了,worklet 也將停,listenersRef 不留殘留
    // 訂閱給下一 take(C4)。
    unsubscribeFrameRef.current?.()
    unsubscribeFrameRef.current = null
    const stream = sharedStreamRef.current
    sharedStreamRef.current = null
    if (stream) {
      for (const track of stream.getTracks()) track.stop()
    }
    // PCM 資源(worklet session / 預熱的 warm AudioContext)綁在麥克風生命週期一起釋放。
    void pcmStream.stop().catch((error) => {
      reportHandledException(error, { source: 'components/landing/dictation-demo-card.tsx' })
      console.warn('[landing-demo] failed to stop PCM on shared-stream release', error)
    })
  }, [clearIdleReleaseTimer, pcmStream.stop])

  /** 重置 warm 麥克風的 30s 閒置釋放計時器(逾時→真正釋放)。 */
  const armIdleReleaseTimer = useCallback(() => {
    clearIdleReleaseTimer()
    idleReleaseTimerRef.current = setTimeout(() => {
      idleReleaseTimerRef.current = null
      // 逾時但擷取仍進行中(如 recorder 已自動停、realtime 仍在錄)→ 別切掉活的擷取;
      // 重新 arm,等真正閒置才釋放。
      if (isCaptureBusy()) {
        armIdleReleaseTimerRef.current()
        return
      }
      releaseSharedStream()
    }, MIC_IDLE_RELEASE_MS)
  }, [clearIdleReleaseTimer, releaseSharedStream, isCaptureBusy])
  armIdleReleaseTimerRef.current = armIdleReleaseTimer

  // unmount:停掉波形 rAF、打字計時器、frame 訂閱與 realtime client。
  useEffect(() => {
    // remount-safe(React StrictMode dev 雙掛載):setup 時重置,免得前一輪 cleanup 的
    // true 卡住同一個 ref、讓 commit/上傳 continuation 永遠拒絕。
    unmountedRef.current = false
    return () => {
      // 標記已卸載:in-flight 的 getUserMedia 於此之後才 resolve 時,commit 會停掉它而非
      // 收進已卸載的 ref(避免麥克風洩漏)。
      unmountedRef.current = true
      // 推進世代,讓任何晚到的 realtime 回呼被守衛擋掉(不對已卸載元件 setState)（H2）。
      sessionGenRef.current += 1
      if (rafRef.current !== null && typeof window !== 'undefined') {
        window.cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      typePoolRef.current?.clear()
      typePoolRef.current = null
      unsubscribeFrameRef.current?.()
      unsubscribeFrameRef.current = null
      realtimeRef.current?.dispose()
      realtimeRef.current = null
      // 清 take 硬上限計時器(免卸載後仍觸發 stopRecording)。
      clearTakeTimer()
      // 釋放共享麥克風(卸載時仍在錄 / warm parked):清閒置計時器、解 'ended' 監聽、停 tracks、
      // 停 PCM。releaseSharedStream 是 stable useCallback(宣告於上),deps 安全。冪等。
      releaseSharedStream()
    }
  }, [releaseSharedStream, clearTakeTimer])

  /** 每幀讀「目前」的音量來源(realtime PCM 或批次 recorder)。 */
  function readActiveLevels(count: number): number[] {
    // H3:在 tick 內判定來源,realtime 中途退回批次時不會卡讀已關閉的 PCM。
    return (realtimeActiveRef.current ? pcmStream.getLevels : recorder.getLevels)(count)
  }

  function startWaveLoop() {
    if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') return
    // reduced-motion:不做動畫,沿用 CSS 的靜態波形
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return
    if (rafRef.current !== null) window.cancelAnimationFrame(rafRef.current)
    setLiveWave(true)
    const tick = () => {
      const bars = waveRef.current?.children
      if (bars && bars.length > 0) {
        const levels = readActiveLevels(bars.length)
        for (let index = 0; index < bars.length; index++) {
          // 純視覺放大 + clamp 到 1(bar 不超出容器);見 WAVE_VISUAL_GAIN。
          const level = Math.min(1, Math.max(0.08, (levels[index] ?? 0) * WAVE_VISUAL_GAIN))
          ;(bars[index] as HTMLElement).style.setProperty('--wh', level.toFixed(2))
        }
      }
      rafRef.current = window.requestAnimationFrame(tick)
    }
    rafRef.current = window.requestAnimationFrame(tick)
  }

  /**
   * 拆掉 realtime 的 frame 橋接與 PCM 擷取(切回批次或結束時)。
   * 注意:這裡「不」清 realtimeActiveRef — 停止後仍需等 finish() 決定
   * 結果(onFinal vs onError→批次接力)。在 finish() 結果出來前保持 active,
   * recorder.onStop 才不會搶先上傳(C2 的單一所有權)。旗標由終局轉換
   * (onFinal 的 done / runBatchSubmit / unavailable→done)負責清除。
   */
  async function teardownRealtime() {
    unsubscribeFrameRef.current?.()
    unsubscribeFrameRef.current = null
    // 先取消訂閱、再 pcmStream.stop()。已知限制:worklet 內 <100ms 的尾巴 frame 一律丟棄
    // (stop() 內 port.onmessage 同步清空,且此處已解訂閱)—— 不影響辨識品質,語尾 <100ms
    // 的損失在對話式語音輸入可忽略。
    await pcmStream.stop()
  }

  /**
   * 批次路徑:上傳已錄好的 WAV,把潤飾結果用打字節奏打進輸出。
   * 單一所有權:同一個世代只會送出一次(batchSubmittedRef 守衛),
   * 讓 realtime 失敗→批次的接力剛好一次上傳(C2)。
   */
  async function runBatchSubmit(blob: Blob) {
    if (batchSubmittedRef.current) return
    batchSubmittedRef.current = true
    // RC3:清掉可能殘留的 error banner —— S12 安全網成功上傳時,結果不該打在舊錯誤橫幅下面。
    setNotice(null)
    // 批次接管後就脫離 realtime;清掉旗標讓後續狀態判斷正確。
    realtimeActiveRef.current = false
    setPhaseSync('processing')
    try {
      const demo = await submitAnonymousDemo(blob)
      // M2:submit pending 中卸載 → 不建 timer pool / 不 setState(避免 updates-after-unmount + timer 洩漏)。
      if (unmountedRef.current) return
      setPhaseSync('typing')
      clearTypePool()
      const pool = createTimerPool()
      typePoolRef.current = pool
      // Plain text — same as the realtime path. The demo surfaces only the
      // finished, polished `text`; it never shows the raw pre-enhancement
      // transcript or a before/after diff.
      typeText(
        pool,
        demo.text,
        TYPE_SPEED_MS,
        (typed) => setOutput(typed),
        () => setPhaseSync('done')
      )
    } catch (error) {
      reportHandledException(error, { source: 'components/landing/dictation-demo-card.tsx' })
      // M2:submit reject 也一樣,卸載後直接退。
      if (unmountedRef.current) return
      // 可見的 recovery:.alert 錯誤文案 + 回到可重試的 done 狀態
      console.warn('[landing-demo] batch demo failed', error)
      const rateLimited =
        error instanceof LandingDemoApiError &&
        (error.code === 'rate_limited' || error.status === 429)
      setNotice(rateLimited ? 'rate-limited' : 'error')
      setPhaseSync('done')
    }
  }

  /**
   * 取得單一共享麥克風擷取(DEMO_MIC_CONSTRAINTS:關閉 EC/NS/AGC,對齊 desktop 原始
   * 擷取)。失敗回 null(不 throw)。
   */
  async function acquireSharedMic(): Promise<MediaStream | null> {
    if (
      typeof navigator === 'undefined' ||
      typeof navigator.mediaDevices?.getUserMedia !== 'function'
    ) {
      return null
    }
    try {
      return await navigator.mediaDevices.getUserMedia(DEMO_MIC_CONSTRAINTS)
    } catch (error) {
      reportHandledException(error, { source: 'components/landing/dictation-demo-card.tsx' })
      // 使用者拒絕或裝置不可用 — 呼叫端顯示錯誤(可見的 recovery)。
      console.warn('[landing-demo] microphone unavailable', error)
      return null
    }
  }

  // 切走分頁(document.hidden)且非錄音中 → 立即釋放 warm stream(park 的麥克風續留無益)。
  // 錄音中(phase === 'recording')或 startRecording 進行中(startInFlightRef,phase 還沒翻)
  // 都不釋放,以免打斷/殺掉正在啟動的擷取。
  useEffect(() => {
    if (typeof document === 'undefined') return
    const onVisibilityChange = () => {
      // 切走分頁且擷取非進行中 → 立即釋放 warm stream;錄音中 / 啟動中不釋放,免打斷擷取。
      if (document.hidden && !isCaptureBusy()) releaseSharedStream()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [releaseSharedStream, isCaptureBusy])

  /** warm stream 是否每條 audio track 都仍 live(可重用);否則須丟掉開新的。 */
  function isWarmStreamUsable(stream: MediaStream): boolean {
    const tracks = stream.getAudioTracks()
    return tracks.length > 0 && tracks.every((track) => track.readyState === 'live')
  }

  /** 回傳仍可用的 warm stream;若 warm stream 的 track 掛了則就地釋放並回 null。 */
  function getUsableWarmStream(): MediaStream | null {
    const warm = sharedStreamRef.current
    if (!warm) return null
    if (isWarmStreamUsable(warm)) return warm
    releaseSharedStream() // track 掛了 → 丟掉開新的
    return null
  }

  /**
   * 收成 warm stream(跨 take 重用)並掛 'ended' 監聽:麥克風被撤權 / 裝置被拔除會讓
   * audio track 'ended',park 中直接丟掉這個 warm stream,下一 take 重新 acquire 一份新的。
   */
  function storeWarmStream(stream: MediaStream) {
    sharedStreamRef.current = stream
    const onEnded = () => {
      console.warn('[landing-demo] shared mic track ended — releasing warm stream')
      releaseSharedStream()
    }
    const tracks = stream.getAudioTracks()
    for (const track of tracks) track.addEventListener('ended', onEnded)
    detachEndedRef.current = () => {
      for (const track of tracks) track.removeEventListener('ended', onEnded)
    }
  }

  /**
   * 把 acquire 到的 stream 收進 warm slot(尚未收過時)。已收過同一條就 no-op;若 slot
   * 已被別條佔用(理論上不會 — 單一擷取不變式)則收掉多餘的那條防呆。回傳目前的 warm stream
   * (元件已卸載則停掉傳入 stream、回 null,呼叫端據此不再啟動擷取)。
   */
  function commitAcquiredStream(stream: MediaStream | null): MediaStream | null {
    if (!stream) return null
    if (unmountedRef.current) {
      // in-flight acquire 於 unmount 後才 resolve:別收進已卸載的 ref(否則麥克風永久洩漏)。
      for (const track of stream.getTracks()) track.stop()
      return null
    }
    const current = sharedStreamRef.current
    if (current === stream) return stream
    if (current) {
      for (const track of stream.getTracks()) track.stop()
      return current
    }
    storeWarmStream(stream)
    return stream
  }

  /** take 結束:保留 warm stream 跨 take 重用,arm 30s 閒置計時器延遲釋放。
   *  但此刻文件已隱藏(切走分頁)則續留無益 → 直接釋放。 */
  function parkSharedStream() {
    if (!sharedStreamRef.current) return
    if (typeof document !== 'undefined' && document.hidden) {
      releaseSharedStream()
      return
    }
    armIdleReleaseTimer()
  }

  /** 進入 'recording' 時 arm take 硬上限(見 TAKE_MAX_DURATION_MS):到期結束整個 take(冪等,靠 stopRecording 重入守衛)。 */
  function armTakeTimer() {
    clearTakeTimer()
    takeTimerRef.current = setTimeout(() => {
      takeTimerRef.current = null
      void stopRecording()
    }, TAKE_MAX_DURATION_MS)
  }

  /**
   * 啟動一次共享麥克風 acquire。**統一擁有 commit/park**:commit/park 的 .then 掛在 RAW acquire
   * 上(所有 acquire —— pointerdown 預熱、startRecording、逾時遲到 —— resolve 後一律正確
   * commit;未被 take 消費就 park,配合 RC2 同步 phaseRef 不吃 stale)。in-flight ref 存 bounded
   * promise(呼叫端 await 的那顆;RC6 acquire 逾時下移至此,warm/in-flight 重用不再白開 timer)。
   * ref 於 RAW settle 時清(getUserMedia 尚在飛時不得開第二個 —— 單一擷取不變式)。
   */
  function beginMicAcquire(): Promise<MediaStream | null> {
    const rawAcquire = acquireSharedMic()
    const bounded = raceWithTimeout(rawAcquire, MIC_ACQUIRE_TIMEOUT_MS, null)
    micAcquirePromiseRef.current = bounded
    void rawAcquire
      .then((stream) => {
        const committed = commitAcquiredStream(stream)
        if (
          committed &&
          !isCaptureBusy() &&
          (phaseRef.current === 'idle' || phaseRef.current === 'done')
        ) {
          parkSharedStream()
        }
      })
      .catch((error) => {
        reportHandledException(error, { source: 'components/landing/dictation-demo-card.tsx' })
        console.warn('[landing-demo] mic acquire failed', error)
      })
      .finally(() => {
        if (micAcquirePromiseRef.current === bounded) micAcquirePromiseRef.current = null
      })
    return bounded
  }

  /**
   * 取得可用的共享麥克風,維持單一擷取不變式:優先重用 warm stream(每條 track 仍 live)、
   * 否則沿用 pointerdown 預熱進行中的 acquire、再否則開一次新的 getUserMedia。永不對同一支
   * 實體麥克風開兩路擷取(會互搶 → 損毀音訊「777777」)。開新 take 時順手取消閒置釋放。
   */
  function obtainSharedMic(): Promise<MediaStream | null> {
    clearIdleReleaseTimer()
    const warm = getUsableWarmStream()
    if (warm) return Promise.resolve(warm)
    if (micAcquirePromiseRef.current) return micAcquirePromiseRef.current
    return beginMicAcquire()
  }

  /** 達上限等需中止的情況:停止擷取、顯示通知、回 idle。單一所有權守衛防誤上傳。 */
  function abortRecording(noticeVal: DemoNotice, gen: number) {
    if (sessionGenRef.current !== gen) return
    // S8:設軟鎖,擋掉 recorder.stop() 觸發的 onStop auto-stop 誤 re-enter stopRecording
    // (onStop 讀 phaseRef,passive effect 未 flush 時仍 'recording')→ 免卡 'processing'。
    stopRequestedRef.current = true
    clearTakeTimer()
    batchSubmittedRef.current = true
    realtimeActiveRef.current = false
    stopWaveLoop()
    recorder.stop()
    // releaseSharedStream 統一收尾:拆 frame 橋接、停 tracks、停 PCM(worklet + warm cache)。
    releaseSharedStream()
    setNotice(noticeVal)
    setPhaseSync('idle')
  }

  /**
   * 開始錄音(tap-to-start):按下即進入錄音,relay 連線在背景進行,不必等連線。
   * 單一麥克風擷取(關 EC/NS/AGC)由 blob recorder 與 PCM worklet 共用 — 避免對同一
   * 支實體麥克風開兩路擷取互搶而收到損毀音訊(曾導致 STT 亂碼「777777」)。麥克風跨 take
   * 保持 warm(pointerdown 可能已預熱),取得後 blob recorder 與 PCM worklet **並行**啟動;
   * frame 橋接先掛(worklet 啟動 → 訂閱之間不丟 frame)。兩路都 settle 才進入錄音狀態,
   * 讓「正在聆聽」提示只在真的在收音時才出現。按下當下起每個 PCM frame 都會被 buffer
   * (session 建立前累積於 pendingFramesRef,建立後補送),連線期間講的話不會遺失。
   */
  async function startRecording() {
    // double-start 上鎖:entry → phase flip 之間的點擊一律被 handleMicClick 忽略。
    // finally 統一解鎖 — 任何未預期 throw(且本函式以 void 呼叫,rejection 無人接)
    // 都不會把鎖卡死、讓按鈕永久失效。
    startInFlightRef.current = true
    try {
      setNotice(null)
      setOutput('')
      capturedBlobRef.current = null
      realtimeActiveRef.current = false
      batchSubmittedRef.current = false
      recorderStartedRef.current = false
      stopRequestedRef.current = false
      pendingFramesRef.current = []
      beginPromiseRef.current = null
      // 每個 take 一份的 blobReady:recorder onStop 於此 resolve 出 WAV,stopRecording 走批次時消費。
      blobReadyRef.current = createDeferred<Blob | null>()
      // 新世代:晚到的前一個 session 回呼會被世代守衛擋掉(H2)。
      sessionGenRef.current += 1
      const gen = sessionGenRef.current
      // 清掉上一輪的 realtime client:否則第二次 take 在新 begin() resolve 前,onFrame 會把
      // frame 推進上一輪「已 finish」的 client(realtimeRef 還指著它)而非 pendingFramesRef,
      // retry 仍吞開頭。dispose 後 realtimeRef 為 null → 新 frame 正確累積、連上後補送新 client。
      realtimeRef.current?.dispose()
      realtimeRef.current = null

      // 通過 guards 後立即進 'starting'(消除「按了卻沒回饋」的死區);兩路擷取 settle 後才 'recording'。
      setPhaseSync('starting')

      // 取得共享麥克風:重用 warm(track 仍 live)/ 沿用 pointerdown 預熱進行中的 acquire /
      // 否則開一次新的。單一擷取不變式:不會對同一支實體麥克風開兩路 getUserMedia。acquire 的
      // 30s 逾時(權限彈窗懸置時不讓 'starting' 永久卡住)已下移到 beginMicAcquire 內(RC6),
      // warm/in-flight 重用不再白開 timer;逾時遲到的 stream 由 raw acquire 的 .then 統一 commit/park。
      const stream = await obtainSharedMic()
      if (sessionGenRef.current !== gen) {
        // 已被新一輪「再試一次」/ 卸載取代 — 收掉本輪新開且未進 warm slot 的 stream。
        if (stream && sharedStreamRef.current !== stream) {
          for (const track of stream.getTracks()) track.stop()
        }
        return
      }
      if (!stream) {
        // 麥克風被拒 / acquire 逾時 → 錯誤通知 + 回 idle;回收 pointerdown 可能已預熱的 warm PCM。
        console.warn('[landing-demo] mic unavailable or acquire timed out — showing error')
        setNotice('error')
        setPhaseSync('idle')
        void pcmStream.stop().catch((error) => {
          reportHandledException(error, {
            source: 'components/landing/dictation-demo-card.tsx',
          })
          console.warn('[landing-demo] failed to stop PCM after mic unavailable', error)
        })
        return
      }
      // 收成 warm stream 跨 take 重用(pointerdown 預熱可能已收好,commit 冪等)。
      // 元件已卸載時 commit 會停掉這條並回 null → 不再啟動擷取(避免麥克風洩漏)。
      const activeStream = commitAcquiredStream(stream)
      if (!activeStream) return

      const pcmSupported = pcmStream.isSupported()

      // frame 橋接先掛(worklet 啟動 → 訂閱之間不丟 frame)。gen 守衛擋掉晚到回呼;
      // session 建立前累積於 pendingFramesRef,建立後直接 pushFrame。
      if (pcmSupported) {
        unsubscribeFrameRef.current = pcmStream.onFrame((frame) => {
          if (sessionGenRef.current !== gen) return
          const client = realtimeRef.current
          if (client) client.pushFrame(frame)
          else pendingFramesRef.current.push(frame)
        })
      }

      // blob recorder 與 PCM worklet **並行**啟動(取代舊的 recorder→pcm 串行,少一段
      // 開頭遺失)。各自保留失敗語意:recorder throw/!ok → warn + recorderStartedRef=false,
      // PCM 仍嘗試;PCM throw → warn 視為 {ok:false};PCM 不支援 → 只跑 recorder。
      const recorderStartPromise = (async () => {
        try {
          const blobStart = await recorder.start(activeStream)
          recorderStartedRef.current = blobStart.ok
          // RC1b:戳上本 take 的 gen,讓 onStop 能辨識並丟棄上一 take 的超遲 blob。
          if (blobStart.ok) recorderGenRef.current = gen
          else {
            console.warn('[landing-demo] blob recorder failed to start on shared stream')
          }
        } catch (error) {
          reportHandledException(error, {
            source: 'components/landing/dictation-demo-card.tsx',
          })
          recorderStartedRef.current = false
          console.warn('[landing-demo] blob recorder threw on start', error)
        }
      })()
      // pcm.start 的逾時/epoch 收尾已內建於 hook(傳 timeoutMs):逾時→hook 內部關 context、
      // 回 {ok:false},下一 take 的 stop() 會 bump epoch 讓任何卡住的 start 自我作廢。此處
      // 只保留失敗語意:throw → warn 視為 {ok:false};不支援 → 只跑 recorder。
      const pcmStartPromise = (async (): Promise<PcmStartResult> => {
        if (!pcmSupported) return { ok: false, reason: 'unsupported' }
        try {
          return await pcmStream.start(activeStream, { timeoutMs: PCM_START_TIMEOUT_MS })
        } catch (error) {
          reportHandledException(error, {
            source: 'components/landing/dictation-demo-card.tsx',
          })
          console.warn('[landing-demo] PCM stream threw on start', error)
          return { ok: false, reason: 'unsupported' }
        }
      })()
      const [, pcmStart] = await Promise.all([recorderStartPromise, pcmStartPromise])

      // await 邊界:已被新一輪「再試一次」/ 卸載取代 — 收掉可能已啟動的 PCM 與 frame 橋接,
      // 別留 stale session/洩漏 context(codex review P2)。此處不 setPhase,提示絕不誤顯示。
      // 只留 gen 檢查:stopRequestedRef 只在 phase==='recording' 由手動/自動停止設定,而本窗
      // (starting)phase 尚未翻到 recording,故 stop-before-ready 於此不可能(該 disjunct 已死)。
      if (sessionGenRef.current !== gen) {
        if (pcmSupported) {
          await pcmStream.stop().catch((error) => {
            reportHandledException(error, {
              source: 'components/landing/dictation-demo-card.tsx',
            })
            // best-effort 收掉這條要丟棄的 PCM session;失敗只記錄(不影響已作廢的這輪)。
            console.warn('[landing-demo] failed to stop PCM on stale start', error)
          })
        }
        unsubscribeFrameRef.current?.()
        unsubscribeFrameRef.current = null
        return
      }

      // 兩路都 settle 且非 stale/stop → 現在才進入錄音(「正在聆聽」提示只在真的在收音時才出現)。
      setPhaseSync('recording')
      startWaveLoop()
      // S2:take 自持硬上限 —— recorder 啟動失敗、只有 realtime 時仍有上限(不無限串流)。
      armTakeTimer()

      if (pcmStart.ok) {
        // PCM 可用 → 背景建立 realtime 連線,連上後補送 pendingFramesRef 累積的 frame。
        beginPromiseRef.current = beginRealtimeInBackground(pcmStart.sampleRate, gen)
      } else if (pcmSupported) {
        // PCM 支援但啟動失敗 → 不會有 frame,拆掉早掛的 frame 橋接免 dangling。
        unsubscribeFrameRef.current?.()
        unsubscribeFrameRef.current = null
      }
    } catch (error) {
      reportHandledException(error, { source: 'components/landing/dictation-demo-card.tsx' })
      // 非預期例外的可見 recovery:記錄 + 完整 teardown(拆 frame 橋接 + 停 PCM)+ park 麥克風
      // (arm 30s 釋放,不讓中途失敗的擷取洩漏)+ 錯誤通知 + 回 idle。
      console.warn('[landing-demo] startRecording failed unexpectedly', error)
      unsubscribeFrameRef.current?.()
      unsubscribeFrameRef.current = null
      void pcmStream.stop().catch((stopError) => {
        reportHandledException(stopError, {
          source: 'components/landing/dictation-demo-card.tsx',
        })
        console.warn('[landing-demo] failed to stop PCM after start error', stopError)
      })
      parkSharedStream()
      setNotice('error')
      setPhaseSync('idle')
    } finally {
      // phase 已翻(或錯誤已收尾)→ 解鎖 double-start(此後的點擊 = 停止 / 重試)。
      startInFlightRef.current = false
    }
  }

  /**
   * 背景建立 realtime client 並連線(不阻塞錄音開始)。回傳 'realtime'(已上線)、
   * 'batch'(退回批次)或 'rate-limited'(達上限,本函式已收尾:通知 + 回 idle)。
   * 連上後把 pendingFramesRef 累積的 frame 一次補送。stopRecording 會 await 這個
   * promise(念一句通常已完成)以決定收尾方式。
   */
  async function beginRealtimeInBackground(
    sampleRate: number,
    gen: number
  ): Promise<'realtime' | 'batch' | 'rate-limited'> {
    const isStale = () => sessionGenRef.current !== gen
    const client = createRealtimeDemoClient({
      sampleRate,
      language: i18n.language,
      callbacks: {
        // 刻意不顯示 partial:那是 enhancement 前的原始 STT 毛胚字。使用者
        // 不該看到未潤飾的中間文字 — 只在最後把潤飾結果打出來。
        onPartial: () => {},
        onFinal: (text) => {
          if (isStale()) return
          realtimeActiveRef.current = false
          // realtime 已產生終局結果 — 鎖批次所有權,避免晚到的 recorder.onStop(此刻
          // realtimeActiveRef 已 false)誤觸發 runBatchSubmit 覆蓋這個結果。
          batchSubmittedRef.current = true
          // 把最終潤飾結果逐字打進輸出(純文字,無 raw/diff),打完才轉 done。
          setOutput('')
          setPhaseSync('typing')
          clearTypePool()
          const pool = createTimerPool()
          typePoolRef.current = pool
          typeText(
            pool,
            text,
            TYPE_SPEED_MS,
            (typed) => setOutput(typed),
            () => setPhaseSync('done')
          )
        },
        onError: (code) => {
          if (isStale()) return
          if (code === 'no_speech') {
            realtimeActiveRef.current = false
            // 同上:鎖批次所有權,避免晚到的 onStop 誤上傳覆蓋 no-speech 終局。
            batchSubmittedRef.current = true
            setNotice('no-speech')
            setOutput('')
            setPhaseSync('done')
          } else {
            // 'unavailable':realtime 中斷 — 走 async 兜底(等 WAV bounded 再上傳),不同步讀
            // capturedBlobRef(finish 快速失敗時 blob 可能還沒到,同步讀會誤走錯誤且之後永不
            // 上傳,S4)。failoverToBatch 內含 realtimeActiveRef 清理 + 單一所有權守衛。
            void failoverToBatch()
          }
        },
      },
    })
    try {
      const live = await client.begin()
      // 不得 commit 活 client 的情況:stale;batch 已取得所有權(batchSubmittedRef);或已發起停止
      // (stopRequestedRef —— S10:10s decision timeout 落到 batch 分支後、batchSubmittedRef 尚未設
      // 的 3s awaitBlob 窗內 begin 才 resolve,舊守衛會放行 → 活 relay session 沒人 finish/dispose,
      // 計量洩漏)。三者任一 → dispose + 'batch'。
      if (isStale() || batchSubmittedRef.current || stopRequestedRef.current) {
        client.dispose()
        return 'batch'
      }
      if (live) {
        realtimeRef.current = client
        realtimeActiveRef.current = true
        // 補送按下當下起、連線期間累積的 frame(session 已 ready,直接送)。
        const pending = pendingFramesRef.current
        pendingFramesRef.current = []
        for (const frame of pending) client.pushFrame(frame)
        return 'realtime'
      }
      client.dispose()
      return 'batch'
    } catch (error) {
      reportHandledException(error, { source: 'components/landing/dictation-demo-card.tsx' })
      client.dispose()
      if (error instanceof RealtimeDemoRateLimitedError) {
        // 達上限:停止背景錄音、顯示通知、回 idle。但 S5:batch 已取得所有權(batchSubmittedRef)
        // 時,遲到的 429 不得覆蓋 batch 終局 → 只 warn,不 abort。
        if (isStale() || batchSubmittedRef.current) {
          console.warn('[landing-demo] late rate-limit after batch ownership — ignoring')
          return 'rate-limited'
        }
        abortRecording('rate-limited', gen)
        return 'rate-limited'
      }
      // begin() 只會 throw rate_limited;其他都回 false。防禦性 fallback。
      console.warn('[landing-demo] realtime begin failed — falling back to batch', error)
      return 'batch'
    }
  }

  /** 等本 take 的 WAV(recorder onStop resolve blobReady)—— bounded:逾時當作沒有 blob。
   *  S3:recorder 沒啟動時 blobReady 永不 resolve,直接回 null(不白等 3s)。 */
  function awaitBlob(): Promise<Blob | null> {
    if (!recorderStartedRef.current) return Promise.resolve(null)
    const ready = blobReadyRef.current?.promise ?? Promise.resolve(capturedBlobRef.current)
    return raceWithTimeout(ready, BLOB_WAIT_TIMEOUT_MS, null)
  }

  /**
   * realtime 失敗(沒有 client、finish() 沒給出終局轉換等)時的兜底:等 WAV(bounded)走批次,
   * 沒有 blob 才顯示一般錯誤。絕不留在沒有終局轉換的 processing(H1)。runBatchSubmit 的
   * 單一所有權守衛確保不重複。
   */
  async function failoverToBatch() {
    realtimeActiveRef.current = false
    const blob = await awaitBlob()
    if (blob) {
      await runBatchSubmit(blob)
    } else {
      setNotice('error')
      setPhaseSync('done')
    }
  }

  /**
   * 停止錄音(單一決策點)。**先停擷取**(不等 begin 決定)—— token fetch 卡住時「按停止」
   * 也要立刻停 mic;再 bounded 地等 begin 決定收尾:'realtime' → finish() 取最終結果;否則
   * 走批次(等 blobReady → runBatchSubmit)。手動點停止與自動停共用此路徑。
   */
  async function stopRecording() {
    // S9:重入守衛 —— take 硬上限計時器、onStop 的 auto-stop、與「React 尚未 commit
    // processing 前的手動點擊」可能雙進入 → double finish()(覆寫 resolver、洩 30s timer)。
    // 單執行緒下先進者已同步設 stopRequestedRef,後進者直接 return。abortRecording(S8)也設此
    // 旗標,故其收尾後的 onStop auto-stop 不會誤 re-enter。
    if (stopRequestedRef.current) return
    stopRequestedRef.current = true
    clearTakeTimer() // 硬上限已由本次停止接手
    const gen = sessionGenRef.current
    stopWaveLoop()
    setPhaseSync('processing')

    // 先停擷取:recorder.stop()(冪等,已自動停時 recorder 已 inactive)→ onStop resolve
    // blobReady;teardownRealtime 拆 frame 橋接 + 停 PCM。
    if (recorderStartedRef.current) recorder.stop()
    await teardownRealtime()

    // begin 決定(bounded):念一句通常已完成;token fetch 卡住 10s 後當作 batch。
    const decision = await raceWithTimeout(
      beginPromiseRef.current ?? Promise.resolve<'realtime' | 'batch' | 'rate-limited'>('batch'),
      BEGIN_DECISION_TIMEOUT_MS,
      'batch'
    )
    if (sessionGenRef.current !== gen) return // 已被新一輪取代
    if (decision === 'rate-limited') return // beginRealtimeInBackground 已收尾(notice + idle)

    if (decision === 'realtime' && realtimeRef.current) {
      await realtimeRef.current.finish()
      // finish() 已決定就會清 realtimeActiveRef;若仍 true 代表沒有終局轉換 → 兜底成批次。
      if (realtimeActiveRef.current) await failoverToBatch()
    } else {
      // 批次(realtime 不可用 / begin 回 'batch' / 決定逾時)。等本 take 的 WAV(bounded)。
      realtimeActiveRef.current = false
      const blob = await awaitBlob()
      if (blob) {
        await runBatchSubmit(blob)
      } else {
        // 沒有 WAV(recorder 沒啟動 / 逾時)→ 避免卡在 processing:顯示錯誤並回 done。
        setNotice('error')
        setPhaseSync('done')
      }
    }
    // 兜底 park 共享麥克風(冪等):保留 tracks 跨 take 重用,arm 30s 閒置計時器。
    parkSharedStream()
  }

  /**
   * pointerdown 預熱:只在「接下來的點擊會開始錄音」時做(尚未接管 / idle / done)。
   * 在使用者手勢內先開 getUserMedia(存進 in-flight ref 供 startRecording 沿用)並預熱
   * PCM pipeline(建 AudioContext + addModule),把「按下即錄音」的開頭延遲降到最低。
   * 手勢內建立對 iOS Safari 的 AudioContext / getUserMedia 才合法。
   */
  function handleMicPointerDown() {
    // startRecording 進行中就別再預熱(避免與正在啟動的擷取搶 in-flight acquire)。
    if (startInFlightRef.current) return
    if (takenOver && phase !== 'idle' && phase !== 'done') return

    const warm = getUsableWarmStream()
    if (warm) {
      // 已有可用 warm stream:重置閒置倒數(使用者正在互動)。真的開始 take 時
      // obtainSharedMic 會清掉此 timer;若最後沒點擊,30s 後仍會釋放,不洩漏。
      armIdleReleaseTimer()
    } else if (!micAcquirePromiseRef.current) {
      // S6:commit/park 的收尾統一在 beginMicAcquire 內(所有 acquire 一致),此處只需發起。
      void beginMicAcquire()
    }

    // 預熱 PCM pipeline(AudioContext + addModule),fire-and-forget(無靜默吞錯)。
    void pcmStream.warmup().catch((error) => {
      reportHandledException(error, { source: 'components/landing/dictation-demo-card.tsx' })
      console.warn('[landing-demo] PCM pipeline warmup failed', error)
    })
  }

  function handleMicClick() {
    // double-start 守衛:startRecording 進行中(entry → phase flip)的點擊一律忽略,
    // 否則會重入 startRecording、以世代跳號丟掉第一段錄音。
    if (startInFlightRef.current) return
    if (!takenOver) {
      // 首次互動:從靜態邀請切到真實收音的狀態顯示。
      // The strongest landing-page "interested" signal — the visitor chose to
      // actually try the product instead of just reading the card.
      trackEvent(ANALYTICS_EVENTS.heroDemoInteract)
      setTakenOver(true)
      void startRecording()
      return
    }
    if (phase === 'recording') {
      void stopRecording()
      return
    }
    // 啟動中 / 處理中 / 打字中:忽略點擊(啟動中另有 startInFlightRef 內鎖,此為防禦)。
    if (phase === 'starting' || phase === 'processing' || phase === 'typing') return
    // 'done' 的「再試一次」= 同一句重錄。提示句刻意不換:輪播是為了讓自動循環不重複,
    // 循環已經沒了;而在使用者正要再念一次時抽換句子,只會害他從頭讀起。
    void startRecording()
  }

  // 唯一的示範句。它刻意寫成一段有反悔、有補充的口語 brain dump(「四點…啊不對,
  // 是四點半」),因為 formatted 整理正是要把這種話變成一份乾淨的編號清單 —— 一句
  // 本來就講得很整齊的話示範不出任何東西。
  const sampleText = t('web:landing.demo.sample')

  // 邀請文案:主鈕永遠寫**動作**,狀態描述交還給下方狀態列。狀態列同時先講清楚
  // 按下去會發生什麼(要開麥克風、但不用註冊 —— 匿名 demo 本來就不必登入),
  // 因為「不知道點了會不會被要求登入」也是不敢點的原因之一。
  const inviteMicLabel = t('web:landing.demo.micInvite')
  const inviteStatusLabel = t('web:landing.demo.statusPermission')

  // 真錄音(takenOver)輸出:realtime 與 batch 都走純文字(只顯示最終潤飾結果)。
  // formatted 模式會回多行(編號清單),換行由 .demo-out .demo-text 的
  // `white-space: pre-line` 保留。
  const liveOutput: React.ReactNode = output

  // 空槽:輸出區常駐可見但留空,只有一支游標在等。空欄位比填滿的欄位更逼人動手,
  // 而且它同時回答了「按下去會發生什麼」—— 那一格會被填滿。這是全卡唯一會動的
  // 元素(2px 游標),取代了過去每 5.8 秒重播一次的循環。
  const showEmptySlot = !takenOver

  let cardState = ''
  let micLabel: string
  let statusLabel: string
  let showOutput: boolean
  let waveLive = false
  let outputContent: React.ReactNode
  if (takenOver) {
    cardState = phase === 'idle' ? '' : phase
    micLabel = t(`web:landing.demo.${MIC_LABEL_KEY[phase]}`)
    statusLabel = t(`web:landing.demo.${STATUS_KEY_BY_PHASE[phase]}`)
    showOutput = output !== ''
    waveLive = liveWave
    outputContent = liveOutput
  } else {
    micLabel = inviteMicLabel
    statusLabel = inviteStatusLabel
    showOutput = true
    outputContent = t('web:landing.demo.outEmpty')
  }

  // 真錄音中才顯示「停止」方塊(可點擊停止);其餘一律維持麥克風 icon(邀請點擊)。
  const showStopIcon = takenOver && phase === 'recording'

  // 舞台格的三選一。三塊都永遠在 DOM 裡，只切 visibility——切 display 會插入或
  // 移除高度，下面整段版面就被推動（CLS 規則 3）。
  const showWave = cardState === 'recording'
  const showProc = cardState === 'processing'
  const showStatus = !showWave && !showProc

  return (
    <div
      id="demo"
      ref={reveal}
      className={cn(
        'demo-card text-left',
        REVEAL_CLASS,
        PAIR_FRAME_CLASS,
        PAIR_GRID_CLASS,
        cardState
      )}
      style={revealDelay(420)}
    >
      <div className="grid grid-cols-[minmax(0,1fr)]">
        <PairLabel>{t('web:landing.demo.dpLabel')}</PairLabel>
        {/* 例句永遠整句顯示，不從空白逐字打出來（CLS 規則 2）。 */}
        <p className={cn('dp-sample mb-5', PAIR_SPEECH_CLASS)}>{sampleText}</p>
        <div className="demo-mic-zone flex flex-col items-start gap-[9px]">
          {/* 指示標:靜止不動,只在還沒被接管時出現。它是全卡唯一用到綠色的地方 ——
              綠給一次性提示、不給常駐主鈕,主鈕維持墨黑跟站上其他 CTA 一致
              (綠上主鈕等於替全站重新定義動作色,那是另一場實驗的問題)。 */}
          {showEmptySlot ? (
            <span className="demo-coach relative ml-0.5 rounded-lg bg-ok px-3 py-1.5 text-sm leading-[1.5] text-background after:absolute after:top-full after:left-[18px] after:border-6 after:border-b-0 after:border-transparent after:border-t-ok after:content-['']">
              {t('web:landing.demo.coachMark')}
            </span>
          ) : null}
          <Button
            variant={showStopIcon ? 'secondary' : 'primary'}
            size="marketing"
            className="demo-mic min-w-[12em] justify-center rounded-full shadow-[0_0_0_5px_var(--bg),0_0_0_6px_var(--border)] transition-[background-color,color,opacity] duration-[280ms] hover:shadow-[0_0_0_3px_var(--bg),0_0_0_4px_var(--fg)] [&>svg]:size-[18px]"
            onPointerDown={handleMicPointerDown}
            onClick={handleMicClick}
            disabled={phase === 'starting' || phase === 'processing' || phase === 'typing'}
          >
            {showStopIcon ? <Square aria-hidden="true" /> : <Mic aria-hidden="true" />}
            <span
              className="demo-mic-label inline-block animate-text-swap motion-reduce:animate-none"
              key={micLabel}
            >
              {micLabel}
            </span>
          </Button>
        </div>
        {/* 舞台格：固定 36px 高，閒置／完成放狀態列，聆聽中換波形、整理中換三點。 */}
        <div className="relative mt-3.5 h-9">
          <div
            ref={waveRef}
            className={cn(
              'demo-wave absolute inset-0 flex items-end gap-[3px]',
              waveLive && 'live',
              showWave ? 'visible' : 'invisible'
            )}
            aria-hidden="true"
          >
            {WAVE_BARS.map((bar, index) => (
              <i
                key={index}
                className={cn(
                  'h-full flex-1 origin-bottom rounded-[2px] bg-foreground opacity-75',
                  // 真錄音模式:--wh 由 JS 依實際音量逐 frame 設定,停用腳本動畫。
                  waveLive
                    ? 'scale-y-(--wh) transition-transform duration-[90ms]'
                    : 'animate-demo-wave [animation-delay:var(--wd)] scale-y-[0.1] motion-reduce:scale-y-50 motion-reduce:animate-none'
                )}
                style={{ '--wd': bar.delay, '--wh': bar.height } as React.CSSProperties}
              />
            ))}
          </div>
          <div
            className={cn(
              'demo-proc absolute inset-0 flex items-center gap-2.5',
              showProc ? 'visible' : 'invisible'
            )}
            aria-hidden="true"
          >
            <span className="dp-dots inline-flex gap-[5px]">
              {['', '[animation-delay:180ms]', '[animation-delay:360ms]'].map((delay, index) => (
                <i
                  key={index}
                  className={cn(
                    'size-[7px] animate-demo-dots rounded-full bg-foreground opacity-25 motion-reduce:animate-none motion-reduce:opacity-60',
                    delay
                  )}
                />
              ))}
            </span>
            <span className="dp-text text-base text-muted">{t('web:landing.demo.procLabel')}</span>
          </div>
          <p
            className={cn(
              'demo-status meta absolute inset-0 flex items-center gap-2 font-mono text-(length:--fs-meta) text-muted',
              showStatus ? 'visible' : 'invisible'
            )}
          >
            <span
              className={cn(
                'dot size-2 flex-none rounded-full bg-foreground',
                cardState === 'done' ? 'opacity-100' : 'opacity-25'
              )}
              aria-hidden="true"
            />
            <span
              className="demo-status-text inline-block min-w-0 animate-text-swap truncate whitespace-nowrap motion-reduce:animate-none"
              key={statusLabel}
            >
              {statusLabel}
            </span>
          </p>
        </div>
      </div>
      <PairArrow />
      <div>
        <PairLabel done>{t('web:landing.demo.outLabel')}</PairLabel>
        <div
          className={cn(
            'demo-out',
            showOutput && 'show',
            PAIR_FIELD_CLASS,
            PAIR_SPEECH_CLASS,
            'grid'
          )}
        >
          {/* 結果區先放一份看不見的完整結果撐住高度，逐字打出的字疊在它上面
              （CLS 規則 4）。兩者同字級，否則打字過程中框會縮放。 */}
          <span
            className="demo-ghost invisible [grid-area:1/1] whitespace-pre-line"
            aria-hidden="true"
          >
            {t('web:landing.demo.outGhost')}
          </span>
          <span className="demo-live [grid-area:1/1]">
            <span
              className={cn('demo-text whitespace-pre-line', showEmptySlot && 'empty text-muted')}
            >
              {outputContent}
            </span>
            <span
              className={cn(
                'demo-caret -mr-1 ml-0.5 inline-block h-[1.05em] w-0.5 align-[-2px] bg-foreground',
                cardState === 'typing' || showEmptySlot
                  ? 'animate-caret-blink motion-reduce:animate-none'
                  : 'invisible',
                showEmptySlot && 'idle-blink'
              )}
              aria-hidden="true"
            />
          </span>
        </div>
      </div>
      {notice ? (
        <p className="alert show [grid-column:1/-1] text-md text-error" role="alert">
          {t(
            notice === 'rate-limited'
              ? 'web:landing.demo.errorRateLimited'
              : notice === 'no-speech'
                ? 'web:landing.demo.errorNoSpeech'
                : 'web:landing.demo.errorGeneric'
          )}
        </p>
      ) : null}
    </div>
  )
}
